import React from "react";
import { toast } from "sonner";
import { ListChecks } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Headline, Kpi, PainBanner, Pill, SectionTitle } from "./PersonaUi";
import { AL, CANDS, MODELS, OBS_F, SHORT, fundChecks, usd } from "./riaFundsData";

const TONE = { REPLACE: "bad", WATCH: "alert", KEEP: "good" };

const replacement = (f, model) => {
  const others = Object.keys(model.funds).filter((x) => x !== f);
  const style = AL.grades[f]?.style;
  const all = CANDS.map((c) => ({
    c, g: AL.grades[c],
    ov: others.length ? others.reduce((s, o) => s + (AL.candidate_overlap[c]?.[o] || 0), 0) / others.length : 0,
  })).filter((x) => x.g && ["A", "B"].includes(x.g.letter)).sort((a, b) => a.ov - b.ov);
  const same = all.filter((x) => x.g.style === style);
  return same.length ? { ...same[0], styleOk: true } : { ...all[0], styleOk: false };
};

const FAIL_TEXT = (g) => ({
  "Active share ≥ 80%": `Active share < 80% (${(g.active_share * 100).toFixed(1)}%)`,
  "Grade ≥ C": `Grade ${g.letter}`,
  "Crowded share < 12%": `Crowded share ${((g.crowded_share || 0) * 100).toFixed(1)}%`,
  "No style drift": "Style drift (sample)",
  "Turnover normal": "Turnover spike (sample)",
});

export default function RiaModelMonitor() {
  const funds = [...new Set(MODELS.flatMap((m) => Object.keys(m.funds)))];
  const rows = funds.map((f) => {
    const ck = fundChecks(f);
    const used = MODELS.filter((m) => m.funds[f]);
    return {
      f, ...ck, used,
      clients: used.reduce((s, m) => s + m.clients, 0),
      dollars: used.reduce((s, m) => s + m.aum * m.funds[f], 0),
      repl: ck.status === "REPLACE" ? replacement(f, used[0]) : null,
    };
  }).sort((a, b) => ["REPLACE", "WATCH", "KEEP"].indexOf(a.status) - ["REPLACE", "WATCH", "KEEP"].indexOf(b.status) || b.dollars - a.dollars);
  const act = rows.filter((r) => r.status === "REPLACE");
  const totC = MODELS.reduce((s, m) => s + m.clients, 0);
  const totA = MODELS.reduce((s, m) => s + m.aum, 0);
  return (
    <div className="space-y-4" data-testid="persona-ria-models">
      <PainBanner testid="persona-ria-models-pain" persona="RIA · MODEL FUND MONITOR"
        pain="I run four models for 300 clients — which fund should I replace, with what, and how many clients does it touch?"
        answer="pass/fail per fund, a low-overlap replacement, clients and dollars affected" />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Headline testid="ria-models-headline">{act.length} funds to replace: {act.map((r) => `${r.f} (${r.clients} clients, ${usd(r.dollars)})`).join(" and ")}</Headline>
          <div className="text-sm text-muted-foreground mt-1">{MODELS.length} models · {totC} clients · {usd(totA)} · fund checks on real data as of {OBS_F}</div>
        </div>
        <Button size="sm" onClick={() => toast("Mockup — rebalance ticket not wired yet")} data-testid="ria-models-ticket-btn"><ListChecks className="w-4 h-4 mr-1" />Create rebalance ticket</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {MODELS.map((m) => {
          const worst = Object.keys(m.funds).map((f) => fundChecks(f).status);
          const st = worst.includes("REPLACE") ? "REPLACE" : worst.includes("WATCH") ? "WATCH" : "KEEP";
          return <Kpi key={m.id} testid={`ria-models-kpi-${m.id}`} label={m.name} value={<Pill tone={TONE[st]}>{st === "REPLACE" ? "ACTION" : st === "WATCH" ? "WATCH" : "OK"}</Pill>} sub={`${m.clients} clients · ${usd(m.aum)} · ${Object.keys(m.funds).join(" / ")}`} />;
        })}
      </div>
      <Card><CardContent className="pt-4 overflow-x-auto">
        <SectionTitle>Every fund used in your models</SectionTitle>
        <table className="w-full text-xs" data-testid="ria-models-table">
          <thead className="border-b border-border text-muted-foreground"><tr>
            <th className="text-left py-1.5 px-2">Fund</th><th className="text-left px-2">Failed checks</th><th className="text-left px-2">Used in</th>
            <th className="text-right px-2">Clients</th><th className="text-right px-2">$ affected</th><th className="text-left px-2">Suggested replacement</th><th className="text-right px-2">Status</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.f} className="border-b border-border/40 align-top" data-testid={`ria-models-row-${r.f}`}>
                <td className="py-2 px-2"><span className="font-mono font-bold">{r.f}</span> <span className="text-[10px] text-muted-foreground">{SHORT[r.f]}</span></td>
                <td className="px-2 text-[11px] text-red-300">{r.checks.filter((c) => c[1] === false).map((c) => FAIL_TEXT(r.g)[c[0]]).join(" · ") || <span className="text-muted-foreground">none</span>}</td>
                <td className="px-2 text-[11px]">{r.used.map((m) => m.name).join(", ")}</td>
                <td className="text-right px-2 font-mono">{r.clients}</td>
                <td className="text-right px-2 font-mono">{usd(r.dollars)}</td>
                <td className="px-2 text-[11px]">{r.repl ? <span><b className="font-mono">{r.repl.c}</b> {SHORT[r.repl.c]} · grade {r.repl.g.letter} · {r.repl.ov.toFixed(0)}% overlap with the rest of the model{!r.repl.styleOk && <span className="text-amber-400"> · different style ({r.repl.g.style}) — screen {r.g.style} peers</span>}</span> : <span className="text-muted-foreground">—</span>}</td>
                <td className="text-right px-2"><Pill tone={TONE[r.status]}>{r.status}</Pill></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
      <Card data-testid="ria-models-ticket"><CardContent className="pt-4">
        <SectionTitle right={<Pill tone="info">TICKET PREVIEW</Pill>}>Rebalance ticket</SectionTitle>
        <div className="space-y-1.5 text-[13px]">
          {act.map((r) => (
            <div key={r.f}>Replace <b className="font-mono">{r.f}</b> with <b className="font-mono">{r.repl?.c}</b> in {r.used.map((m) => m.name).join(", ")} — {r.clients} accounts · {usd(r.dollars)} · review tax lots and wash-sale windows first.</div>
          ))}
        </div>
        <div className="text-[10px] text-muted-foreground mt-3">Real: active share, grade, crowded share, holdings overlap ({OBS_F}). Sample: models, clients, AUM, style-drift / turnover flags. Replacement = best-graded (A/B) same-style candidate with the lowest overlap with the model's other funds (flagged when no same-style candidate exists in the sample).</div>
      </CardContent></Card>
    </div>
  );
}
